/** Typed EXIF extraction via exifr, normalized into our ExifData sidecar shape. */
import exifr from 'exifr';
import type { ExifData } from './types.ts';

/**
 * Fields we ask exifr to surface via the fast pick-based path. NOTE: GPS is
 * NOT in this list on purpose — exifr 7 refuses to emit the top-level
 * `latitude`/`longitude` when `pick` is used, even with `gps: true`. GPS is
 * fetched via a separate `exifr.gps()` call below and merged in.
 */
const PICK = [
  'DateTimeOriginal',
  'CreateDate',
  'Make',
  'Model',
  'LensModel',
  'ExifImageWidth',
  'ExifImageHeight',
  'PixelXDimension',
  'PixelYDimension',
] as const;

interface RawExif {
  DateTimeOriginal?: Date | string;
  CreateDate?: Date | string;
  Make?: string;
  Model?: string;
  LensModel?: string;
  ExifImageWidth?: number;
  ExifImageHeight?: number;
  PixelXDimension?: number;
  PixelYDimension?: number;
}

interface RawGps {
  latitude?: number | null;
  longitude?: number | null;
}

function toIso(value: Date | string | undefined): string | undefined {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function joinCamera(make?: string, model?: string): string | undefined {
  const m = make?.trim();
  const mod = model?.trim();
  if (!m && !mod) return undefined;
  if (m && mod && mod.startsWith(m)) return mod;
  return [m, mod].filter(Boolean).join(' ').trim() || undefined;
}

function normalize(raw: RawExif | undefined, gps: RawGps | null | undefined): ExifData {
  const data: ExifData = { v: 1 };

  if (raw) {
    const date = toIso(raw.DateTimeOriginal) ?? toIso(raw.CreateDate);
    if (date) data.dateTaken = date;

    const camera = joinCamera(raw.Make, raw.Model);
    if (camera) data.camera = camera;
    if (raw.LensModel?.trim()) data.lens = raw.LensModel.trim();

    const width = raw.ExifImageWidth ?? raw.PixelXDimension;
    const height = raw.ExifImageHeight ?? raw.PixelYDimension;
    if (typeof width === 'number') data.width = width;
    if (typeof height === 'number') data.height = height;
  }

  if (
    gps &&
    typeof gps.latitude === 'number' &&
    typeof gps.longitude === 'number' &&
    Number.isFinite(gps.latitude) &&
    Number.isFinite(gps.longitude) &&
    !(gps.latitude === 0 && gps.longitude === 0)
  ) {
    data.gps = { lat: gps.latitude, lng: gps.longitude };
  }

  return data;
}

/**
 * Parse EXIF from a File (or Blob). Returns a normalized, sidecar-ready object.
 * Never throws — unreadable/absent EXIF yields `{ v: 1 }`.
 *
 * IMPORTANT: call this on the ORIGINAL file before any canvas re-encode;
 * canvas compression strips EXIF (including GPS).
 */
export async function extractExif(file: Blob): Promise<ExifData> {
  // exifr's `pick` path is fast (only reads the tags we ask for) but silently
  // omits GPS derived-fields even with `gps: true`. So we make TWO calls in
  // parallel: the pick path for metadata, and exifr.gps() which explicitly
  // resolves latitude/longitude. Then merge. Verified locally against real
  // iPhone JPEGs — this pattern actually populates the map.
  try {
    const [meta, gps] = await Promise.all([
      exifr.parse(file, { pick: PICK as unknown as string[] }) as Promise<RawExif | undefined>,
      exifr.gps(file).catch(() => null) as Promise<RawGps | null | undefined>,
    ]);
    return normalize(meta, gps);
  } catch {
    return { v: 1 };
  }
}

/** True when the EXIF carries usable geolocation. */
export function hasGps(exif: ExifData | undefined): exif is ExifData & { gps: { lat: number; lng: number } } {
  return !!exif?.gps;
}

/** Best capture time as epoch-ms, or null. */
export function exifTimestamp(exif: ExifData | undefined): number | null {
  if (!exif?.dateTaken) return null;
  const ms = new Date(exif.dateTaken).getTime();
  return Number.isFinite(ms) ? ms : null;
}
